import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Filler,
  Tooltip,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useRef, useMemo, useCallback } from 'react';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  TimeScale,
  Filler,
  Tooltip,
  zoomPlugin
);

export interface SensorDataset {
  label: string;
  data: Array<{ timestamp: string; value: number | null }>;
  color?: string;
}

export interface SensorChartProps {
  /** Single dataset (legacy) or multiple datasets */
  data: Array<{ timestamp: string; value: number | null }>;
  /** Multiple datasets with labels and colors */
  datasets?: SensorDataset[];
  startTime: string;
  endTime: string;
  unit?: string;
  color?: string;
  height?: number;
  isLoading?: boolean;
  onZoom?: (start: Date, end: Date) => void;
  onResetZoom?: () => void;
}

// Balanced palette: calm for dark mode but with sufficient contrast
const DEFAULT_CHART_COLORS = [
  '#6A9FD4', // Calm blue - clear primary
  '#D4A56A', // Muted ochre - warm contrast without being aggressive
  '#8FBCB0', // Soft teal
  '#B8A0C8', // Dusty lavender
  '#9CB894', // Sage green
  '#C9A090', // Dusty coral
  '#7AA8B8', // Steel blue
  '#C4B090', // Warm sand
];

const DEFAULT_CHART_COLOR = '#6b7280'; // gray-500

export function SensorChart({
  data,
  datasets,
  startTime,
  endTime,
  unit: _unit,
  color,
  height,
  isLoading,
  onZoom,
  onResetZoom,
}: SensorChartProps) {
  // When height is undefined, use 100% to fill the container
  const heightStyle = height !== undefined ? { height } : { height: '100%' };
  const chartRef = useRef<ChartJS<'line'>>(null);

  const chartColor = color || DEFAULT_CHART_COLOR;
  const hasMultipleDatasets = datasets && datasets.length > 0;

  // Handle zoom complete - called by the zoom plugin
  const handleZoomComplete = useCallback(
    ({ chart }: { chart: ChartJS }) => {
      if (!onZoom) return;

      const xScale = chart.scales['x'];
      if (!xScale) return;

      const min = xScale.min;
      const max = xScale.max;

      if (min !== undefined && max !== undefined && max - min > 1000) {
        onZoom(new Date(min), new Date(max));
      }
    },
    [onZoom]
  );

  // Handle double-click to reset
  const handleDoubleClick = useCallback(() => {
    if (onResetZoom) {
      onResetZoom();
    }
  }, [onResetZoom]);

  // Convert data to Chart.js format
  const chartData = useMemo(() => {
    if (hasMultipleDatasets && datasets) {
      // Multiple datasets - each gets its own line
      return {
        datasets: datasets.map((dataset, index) => {
          const dsColor =
            dataset.color || DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length];
          return {
            label: dataset.label,
            data: dataset.data.map((point) => ({
              x: new Date(point.timestamp).getTime(),
              y: point.value,
            })),
            borderColor: dsColor,
            backgroundColor: `${dsColor}1a`,
            fill: false, // Don't fill for multiple lines to avoid visual clutter
            tension: 0,
            pointRadius: 0,
            borderWidth: 2,
          };
        }),
      };
    }

    // Single dataset (legacy behavior)
    return {
      datasets: [
        {
          data: data.map((point) => ({
            x: new Date(point.timestamp).getTime(),
            y: point.value,
          })),
          borderColor: chartColor,
          backgroundColor: `${chartColor}1a`,
          fill: true,
          tension: 0,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    };
  }, [data, datasets, hasMultipleDatasets, chartColor]);

  // Chart options - memoized with time bounds
  const options = useMemo(() => {
    const startMs = new Date(startTime).getTime();
    const endMs = new Date(endTime).getTime();
    const isDark = document.documentElement.classList.contains('dark');
    const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)';
    const textColor = isDark ? '#a1a1aa' : '#6b7280';

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false as const,
      layout: {
        padding: {
          left: 8,
        },
      },
      scales: {
        x: {
          type: 'time' as const,
          min: startMs,
          max: endMs,
          grid: {
            color: gridColor,
          },
          ticks: {
            color: textColor,
            font: { size: 11 },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 12,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: gridColor,
          },
          ticks: {
            color: textColor,
            font: { size: 11 },
            callback: function (
              this: { ticks?: Array<{ value: number }> },
              value: number | string
            ) {
              const v = Number(value);
              if (Number.isInteger(v)) return String(v);

              // Determine appropriate decimal places based on tick step size
              const ticks = this.ticks;
              if (ticks && ticks.length >= 2 && ticks[0] && ticks[1]) {
                const step = Math.abs(ticks[1].value - ticks[0].value);
                if (step > 0 && step < 1) {
                  // Calculate decimals needed: -floor(log10(step))
                  const decimals = Math.min(6, Math.max(1, Math.ceil(-Math.log10(step))));
                  return v.toFixed(decimals);
                }
              }
              return v.toFixed(1);
            },
          },
        },
      },
      plugins: {
        legend: {
          display: hasMultipleDatasets,
          position: 'top' as const,
          labels: {
            boxWidth: 12,
            padding: 8,
            font: { size: 11 },
            color: textColor,
          },
        },
        tooltip: {
          enabled: true,
          mode: 'index' as const,
          intersect: false,
        },
        zoom: {
          zoom: {
            drag: {
              enabled: true,
              backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
            },
            mode: 'x' as const,
            onZoomComplete: handleZoomComplete,
          },
        },
      },
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      onDblClick: handleDoubleClick,
    };
  }, [startTime, endTime, hasMultipleDatasets, handleZoomComplete, handleDoubleClick]);

  if (isLoading) {
    return (
      <div
        style={heightStyle}
        className="flex items-center justify-center rounded-lg bg-gray-50 dark:bg-zinc-900"
      >
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <div style={heightStyle} onDoubleClick={handleDoubleClick}>
        <Line ref={chartRef} data={chartData} options={options} />
      </div>
      {((hasMultipleDatasets && datasets?.every((ds) => ds.data.length === 0)) ||
        (!hasMultipleDatasets && data.length === 0)) && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-sm text-gray-500 dark:text-zinc-500">No data available</p>
        </div>
      )}
    </div>
  );
}
